import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-deadlines-type-3';

/**
 * Friendly icon keywords accepted on `item.icon`, mapped to full `me-icon` names.
 * Any value not listed here is passed through to `me-icon` unchanged (so full
 * icon names like `interface--check` still work).
 */
const ICON_ALIASES: Record<string, string> = {
  check: 'interface--check',
  doublecheck: 'interface--check-double',
  refresh: 'interface--refresh',
};

/**
 * Timeline section displaying date-stamped events in a vertical list.
 * Header (heading + subtitle) above a single-column vertical list, each entry pairing an icon box with a bold date label and description.
 * Differs from sibling deadlines by using contextual icons and date labels rather than numbered step indicators.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for sequential timeline for {keyword}. 6-10 words.
 * @slot subtitle - {content} <me-text variant="large"> - Introductory paragraph for the horizontal steps. You MUST write 15-30 words.
 * @example
 *   <sn-deadlines-type-3
 *     items='[{"date":"Nov 14 2025","text":"Account setup completed.","icon":"check"},{"date":"Dec 01 2025","text":"First document signed.","icon":"doublecheck"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Key Dates</me-heading>
 *     <me-text slot="subtitle" variant="large">Important milestones.</me-text>
 *   </sn-deadlines-type-3>
 */
@Component({
  tag: 'sn-deadlines-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcTimelineList {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Timeline items with date and text or plain strings.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} date - Date or phase label. 2-10 words.
   * @item.property {string} text - Brief description of this phase or obligation. 5-20 words.
   * @item.property {string} icon - Optional icon override. Accepts the keywords "check", "doublecheck", or "refresh" (or a full me-icon name). When omitted, an icon is chosen automatically by position.
   * @item.content date text
   * @minItems 8
   * @maxItems 8
   */
  @Prop({ mutable: true }) items?: (string | { date?: string; text?: string; icon?: string })[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} items - Fallback timeline items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        items?: (string | { date?: string; text?: string; icon?: string })[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  private getIcon(index: number, total: number): string {
    const ratio = index / total;
    if (ratio < 0.4) return 'interface--check-double';
    if (ratio < 0.6) return 'interface--check';
    return 'interface--refresh';
  }

  /**
   * Resolve an explicit `item.icon` to a full `me-icon` name.
   * Accepts the friendly keywords `check` / `doublecheck` / `refresh`, and
   * passes any other value through unchanged. Returns `undefined` when no icon
   * is set, so the caller falls back to the positional `getIcon` logic.
   */
  private resolveIcon(icon?: string): string | undefined {
    if (!icon) return undefined;
    return ICON_ALIASES[icon.toLowerCase()] ?? icon;
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__header`}>
          <slot name="heading"></slot>
          <slot name="subtitle"></slot>
        </div>
        {items.length > 0 && (
          <div class={`${COMPONENT_NAME}__list`}>
            {items.map((item, index) => {
              const isObject = typeof item === 'object';
              const iconName = (isObject ? this.resolveIcon(item.icon) : undefined) ?? this.getIcon(index, items.length);
              return (
                <div class={`${COMPONENT_NAME}__item`}>
                  <div class={`${COMPONENT_NAME}__indicator`}>
                    <div class={`${COMPONENT_NAME}__icon-box`}>
                      <me-icon name={iconName} class={`${COMPONENT_NAME}__icon`} width="20" height="20" lazy={false}></me-icon>
                    </div>
                    {index < items.length - 1 && <span class={`${COMPONENT_NAME}__connector`} aria-hidden="true"></span>}
                  </div>
                  <div class={`${COMPONENT_NAME}__content`}>
                    {isObject ? ([
                      item.date && <me-heading tag="h4" class={`${COMPONENT_NAME}__date`}>{item.date}:</me-heading>,
                      <me-text tag="span"> {item.text}</me-text>,
                    ]) : (
                      <me-text tag="span">{item}</me-text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </me-section>
    );
  }
}
